/**
 * NewsletterSubscribe.tsx — Newsletter Subscription
 * 
 * Calls Magento's subscribeEmailToNewsletter GraphQL mutation.
 * Config: placeholder, buttonText, layout, showPrivacyNote
 */
import React, { useState } from 'react';
import { getGraphQLHeaders, getGraphQLEndpoint } from '@agentic/magento-client/lib/storeConfig';

interface NewsletterSubscribeProps {
  data: {
    placeholder?: string;
    buttonText?: string;
    layout?: string;                  // 'inline' | 'stacked' | 'split-image' | 'footer-strip'
    showPrivacyNote?: boolean;
    buttonMinWidth?: string;          // e.g. '140px' — fixes pill button text-clipping issue
    title?: string;
    description?: string;
    cardSurface?: string;             // 'none' | 'glass' | 'elevated' | 'bordered'
    [key: string]: unknown;
  };
  styles?: string;
}

const SUBSCRIBE_MUTATION = `
  mutation SubscribeNewsletter($email: String!) {
    subscribeEmailToNewsletter(email: $email) {
      status
    }
  }
`;

export const NewsletterSubscribe: React.FC<NewsletterSubscribeProps> = ({ data, styles }) => {
  const {
    placeholder = 'Your email',
    buttonText = 'Subscribe',
    layout: layoutRaw = 'inline',     // legacy: 'inline' | 'stacked' | 'split-image' | 'footer-strip'
    showPrivacyNote = true,
    buttonMinWidth = '120px',
    title,
    description,
    variant: variantRaw,              // NEW: 'inline-form' | 'stacked-form' | 'split-card' | 'minimal-link'
    minimalLinkHref = '/newsletter',
    minimalLinkLabel = 'Subscribe to our newsletter →',
  } = (data ?? {}) as NewsletterSubscribeProps['data'] & { variant?: string; minimalLinkHref?: string; minimalLinkLabel?: string };

  // Variant resolution. New `variant` field takes precedence; legacy `layout`
  // is a fallback. Map variant → effective layout for the existing form render.
  const variantToLayout: Record<string, string> = {
    'inline-form': 'inline',
    'stacked-form': 'stacked',
    'split-card': 'split-image',
  };
  const variant = variantRaw && typeof variantRaw === 'string' ? variantRaw : '';
  const layout = variantToLayout[variant] ?? layoutRaw;
  const isMinimalLink = variant === 'minimal-link';
  const isSplitCard = variant === 'split-card';
  // Full-width banner: title + description + form, all centered, spanning the
  // row. Replaces the old "newsletter component + 2 separate CMS blocks" combo
  // — set `title`/`description` in block settings instead of authoring CMS
  // blocks for the heading + blurb.
  const isFullWidth = variant === 'full-width' || variant === 'banner' || layout === 'full-width' || layout === 'footer-strip';
  const isStacked = layout === 'stacked' || layout === 'split-image';
  const flexDirClass = isStacked ? 'flex-col gap-3' : 'flex-row gap-3';
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    try {
      const endpoint = getGraphQLEndpoint();
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: getGraphQLHeaders(),
        body: JSON.stringify({ query: SUBSCRIBE_MUTATION, variables: { email: email.trim() } }),
      });
      interface SubscribeJson { errors?: Array<{ message?: string }>; data?: unknown }
      const json = await res.json() as SubscribeJson;

      if (json.errors) {
        setStatus('error');
        setMessage(json.errors[0]?.message ?? 'Subscription failed');
      } else {
        setStatus('success');
        setMessage('Successfully subscribed!');
        setEmail('');
      }
    } catch {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  // Variant: minimal-link — just an underlined link that navigates to a
  // dedicated newsletter signup page or opens an external form. No inline
  // form, no validation client-side. Tiny footer-row pattern.
  if (isMinimalLink) {
    return (
      <a
        href={minimalLinkHref}
        className={`inline-flex items-center gap-2 text-sm font-semibold transition-colors ${styles ?? ''}`}
        style={{
          color: 'var(--style-link-color, var(--color-footer-link, var(--accent-primary)))',
          textDecoration: 'underline',
          textUnderlineOffset: '4px',
          textDecorationThickness: '1px',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
        <span>{minimalLinkLabel}</span>
      </a>
    );
  }

  // Variant: split-card — wrap the form in a card surface (style-preset driven).
  // The card styling comes entirely from style preset tokens — no hardcoded
  // values. Theme preset's footer colours apply via inherited token cascade.
  const cardWrapperStyle: React.CSSProperties | undefined = isSplitCard ? {
    background: 'var(--style-card-bg, var(--surface-2))',
    border: 'var(--style-card-border, 1px solid var(--glass-border))',
    borderRadius: 'var(--style-card-radius, 16px)',
    boxShadow: 'var(--style-card-shadow, none)',
    backdropFilter: 'var(--style-backdrop-filter, none)',
    padding: 'clamp(20px, 3vw, 32px)',
  } : undefined;

  // Full-width banner centers the whole block and constrains the inner content
  // + form to readable widths so they don't stretch edge-to-edge on wide rows.
  const outerStyle: React.CSSProperties | undefined = isFullWidth
    ? { ...(cardWrapperStyle ?? {}), textAlign: 'center', width: '100%' }
    : cardWrapperStyle;
  const innerStyle: React.CSSProperties | undefined = isFullWidth
    ? { maxWidth: '720px', margin: '0 auto' }
    : undefined;
  const formStyle: React.CSSProperties | undefined = isFullWidth
    ? { maxWidth: '480px', margin: '0 auto', width: '100%' }
    : undefined;

  return (
    <div className={styles ?? ''} style={outerStyle}>
      <style dangerouslySetInnerHTML={{ __html: `
        .newsletter-input:focus {
          border: var(--style-input-border-focus, 1px solid var(--accent-primary));
          box-shadow: var(--style-focus-ring, 0 0 0 2px var(--accent-primary, rgba(251,124,0,0.3)));
        }
        .newsletter-input::placeholder {
          color: var(--style-input-placeholder-color, var(--text-muted));
        }
      ` }} />
      <div style={innerStyle}>
      {(title || description) && (
        <div className={`newsletter-headline ${isFullWidth ? 'mb-5' : 'mb-3'}`}>
          {title && <h3 className={`${isFullWidth ? 'text-2xl md:text-3xl mb-2' : 'text-lg mb-1'} font-semibold`} style={{ color: 'var(--cms-fc-heading-color, var(--color-text-primary))' }}>{title}</h3>}
          {description && <p className={isFullWidth ? 'text-sm md:text-base max-w-xl mx-auto' : 'text-sm'} style={{ color: 'var(--color-text-muted)' }}>{description}</p>}
        </div>
      )}
      <form onSubmit={(...args) => { void handleSubmit(...args); }} className={`newsletter-form flex ${flexDirClass}`} style={formStyle}>
        <input
          type="email"
          name="email"
          autoComplete="email"
          aria-label="Email address for newsletter"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
          placeholder={placeholder}
          disabled={status === 'loading'}
          className="newsletter-input flex-1 px-5 py-3 text-sm outline-none transition-all"
          style={{
            background: 'var(--style-input-bg, var(--surface-1))',
            border: 'var(--style-input-border, 1px solid var(--glass-border))',
            borderRadius: 'var(--style-input-radius, var(--style-btn-radius, 8px))',
            color: 'var(--style-input-color, var(--text-primary))',
            boxShadow: 'var(--style-input-shadow, none)',
          }}
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          // Background painted via inline style (rather than a Tailwind
          // arbitrary class) so Lighthouse's contrast analyser doesn't
          // pick up a stale static class fallback. The CSS var resolves
          // to #7c2d12 (light) / #1e40af (dark) — both AA white-on-color.
          // No fallback colour needed; --accent-button-strong is always
          // defined at :root in _layer-base.css.
          className="newsletter-btn px-7 py-3 text-sm font-bold tracking-wide uppercase hover:opacity-90 disabled:opacity-50 transition-all whitespace-nowrap"
          style={{
            backgroundColor: 'var(--color-cta-primary-bg, var(--accent-button-strong))',
            color: 'var(--color-cta-primary-text, var(--color-button-text, #ffffff))',
            borderRadius: 'var(--style-btn-radius, 8px)',
            minWidth: buttonMinWidth,
          }}
        >
          {status === 'loading' ? '...' : buttonText}
        </button>
      </form>
      {status === 'success' && <p role="status" className="text-xs mt-3" style={{ color: 'var(--color-success, #22c55e)' }}>{message}</p>}
      {status === 'error' && <p role="alert" className="text-xs mt-3" style={{ color: 'var(--color-error, #ef4444)' }}>{message}</p>}
      {showPrivacyNote && status === 'idle' && <p className="text-xs mt-3" style={{ color: 'var(--color-footer-text, var(--text-muted))', opacity: 0.7 }}>By subscribing, you agree to our Privacy Policy.</p>}
      </div>
    </div>
  );
};

export default NewsletterSubscribe;
